import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Card, Form, InputGroup, Spinner } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { AxiosError } from "axios";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiCodeBraces } from "@mdi/js";
import { useMutation, useQuery } from "react-query";
import Select from "react-select";

import { useToast } from "../../context/ToastContext";
import { getVariaveisListar, postVariaveisCadastrar, postVariaveisAtualizar } from "../../config/swagger";
import type { CadastrarVariavelRequest } from "../../model/cadastrarVariavelRequest";
import type { AtualizarVariavelRequest } from "../../model/atualizarVariavelRequest";
import type { CadastrarVariavelResponseIResponse } from "../../model/cadastrarVariavelResponseIResponse";
import type { AtualizarVariavelResponseIResponse } from "../../model/atualizarVariavelResponseIResponse";

import Layout from "../../components/Layout";
import { SelectStyle } from "../../config/select";

const toastTitle = "Variável";

const tipoOptions = [
	{ value: "text", label: "Texto" },
	{ value: "secret", label: "Secreta" },
];

type VariavelFormData = {
	id?: number;
	nome?: string | null;
	valor?: string | null;
	tipo?: string | null;
	validade?: number | null;
};

export default function VariavelFormulario() {
	const navigate = useNavigate();
	const { id } = useParams();
	const { handleToast } = useToast();

	const [formStatus, setFormStatus] = useState(id ? false : true);
	const [formSaving, setFormSaving] = useState(false);

	const { data, isLoading, isFetching, isRefetching } = useQuery(["variavel", id], () => fetchData(id!), { enabled: !!id && !formStatus });

	const { register, handleSubmit, control, reset } = useForm<VariavelFormData>({ defaultValues: data });

	const mutationPost = useMutation(mutatePost, { onSuccess: mutatePostSuccess, onError: mutateError });
	const mutationPut = useMutation(mutatePut, { onSuccess: mutatePutSuccess, onError: mutateError });

	async function fetchData(id: string) {
		const resp = await getVariaveisListar();
		const variavel = resp.Data?.find((v) => v.id === Number(id));
		if (variavel) {
			reset(variavel);
		}
		return variavel;
	}

	async function mutatePost(data: CadastrarVariavelRequest) {
		setFormSaving(true);
		return await postVariaveisCadastrar(data);
	}

	function mutatePostSuccess(resp: CadastrarVariavelResponseIResponse) {
		handleToast(toastTitle, "Informações salvas com sucesso!", 5000);
		if (resp.Data?.id) {
			navigate("/variavelFormulario/" + resp.Data.id);
		}
		setFormStatus(false);
		setFormSaving(false);
	}

	async function mutatePut(data: AtualizarVariavelRequest) {
		setFormSaving(true);
		return await postVariaveisAtualizar(data);
	}

	function mutatePutSuccess(resp: AtualizarVariavelResponseIResponse) {
		handleToast(toastTitle, "Informações salvas com sucesso!", 5000);
		setFormStatus(false);
		setFormSaving(false);
	}

	function mutateError(resp: AxiosError) {
		handleToast(toastTitle, `Problema na operação: ${resp.message}`, 5000, "danger");
		setFormSaving(false);
	}

	return (
		<Layout>
			<Form
				onSubmit={handleSubmit((values) => {
					if (id) {
						mutationPut.mutate({
							id: Number(id),
							valor: values.valor,
							validade: values.validade,
						});
					} else {
						mutationPost.mutate({
							nome: values.nome,
							valor: values.valor,
							tipo: values.tipo,
							validade: values.validade,
						});
					}
				})}
			>
				<h5 className="mt-4 mb-4 d-flex align-items-center fw-light">
					<Link to={"/variaveis"} className="d-flex align-items-center text-decoration-none">
						<Icon path={mdiChevronLeft} size={1} className="me-1" /> <Icon path={mdiCodeBraces} size={1} className="me-2" /> Formulário de Variável
					</Link>
					{(isLoading || isFetching || isRefetching) && <Spinner size="sm" className="ms-1" variant="secondary" />}
					<div className="d-flex gap-2 float-right ms-auto" style={{ marginTop: -10, marginBottom: -10 }}>
						{!formStatus && (
							<Button
								variant="dark"
								className="rounded-3 shadow-sm"
								onClick={() => {
									setFormStatus(true);
								}}
							>
								Editar Informações
							</Button>
						)}
						{formStatus && (
							<>
								<Button
									variant="light"
									className="rounded-3 shadow-sm"
									type="button"
									onClick={() => {
										if (id) {
											setFormStatus(false);
										} else {
											navigate("/variaveis");
										}
									}}
									disabled={formSaving}
								>
									Cancelar
								</Button>
								<Button variant="primary" className="rounded-3 shadow-sm" type="submit" disabled={formSaving}>
									{formSaving ? (
										<>
											<Spinner animation="border" size="sm" className="me-2" /> Salvando
										</>
									) : (
										"Salvar Informações"
									)}
								</Button>
							</>
						)}
					</div>
				</h5>

				<Card className="mb-3">
					<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">Informações Básicas</Card.Header>
					<Card.Body>
						<Form.Group className="mb-3" controlId="nome">
							<Form.Label>Nome</Form.Label>
							<Form.Control type="text" placeholder="Informe aqui o nome" {...register("nome", { required: !id })} disabled={!formStatus || !!id} />
							{id && <Form.Text className="text-muted">O nome não pode ser alterado após o cadastro</Form.Text>}
						</Form.Group>
						<Form.Group className="mb-3" controlId="valor">
							<Form.Label>Valor</Form.Label>
							<Form.Control type="text" placeholder="Informe aqui o valor" {...register("valor")} disabled={!formStatus} />
							{id && data?.tipo === "secret" && <Form.Text className="text-muted">Não altere este campo para manter o valor atual</Form.Text>}
						</Form.Group>
						<Form.Group className="mb-3" controlId="tipo">
							<Form.Label>Tipo</Form.Label>
							<Controller
								name="tipo"
								control={control}
								render={({ field }) => (
									<Select
										options={tipoOptions}
										value={tipoOptions.find((opt) => opt.value === field.value) || null}
										onChange={(selected) => field.onChange(selected?.value || null)}
										isDisabled={!formStatus || !!id}
										styles={SelectStyle}
										placeholder="Selecione o tipo..."
										noOptionsMessage={() => "Nenhum tipo disponível"}
									/>
								)}
							/>
							<Form.Text className="text-muted">{id ? "O tipo não pode ser alterado após o cadastro" : 'O tipo "Secreta" não exibirá o valor da variável após cadastro'}</Form.Text>
						</Form.Group>
						<Form.Group className="mb-3" controlId="validade">
							<Form.Label>Validade</Form.Label>
							<InputGroup>
								<Form.Control
									type="number"
									placeholder="Informe aqui a validade"
									{...register("validade", { setValueAs: (v) => (v === "" ? null : Number(v)) })}
									disabled={!formStatus}
								/>
								<InputGroup.Text>segundos</InputGroup.Text>
							</InputGroup>
							<Form.Text className="text-muted">Deixe vazio para a variável não expirar</Form.Text>
						</Form.Group>
					</Card.Body>
				</Card>

				<div className="d-flex gap-2 mb-4">
					{!formStatus && (
						<Button
							variant="dark"
							className="rounded-3 shadow-sm"
							onClick={() => {
								setFormStatus(true);
							}}
						>
							Editar Informações
						</Button>
					)}
					{formStatus && (
						<>
							<Button variant="primary" className="rounded-3 shadow-sm" type="submit" disabled={formSaving}>
								{formSaving ? (
									<>
										<Spinner animation="border" size="sm" className="me-2" /> Salvando
									</>
								) : (
									"Salvar Informações"
								)}
							</Button>
							<Button
								variant="light"
								className="rounded-3 shadow-sm"
								type="button"
								onClick={() => {
									if (id) {
										setFormStatus(false);
									} else {
										navigate("/variaveis");
									}
								}}
								disabled={formSaving}
							>
								Cancelar
							</Button>
						</>
					)}
				</div>
			</Form>
		</Layout>
	);
}
